import { API } from '~/data/api'
import { ANALYTICS_EVENTS } from '~/types/analyticsEvents'

import type {
  FacebookAuthResponse,
  GoogleAuthResponse,
  SocialAuthenticationResult
} from '~/types/auth'

export const useAuthenticationService = () => {
  const config = useRuntimeConfig()
  const authStore = useAuthStore()
  const { trackEvent } = useAnalyticsTrackingService()

  const facebookAppId = config.public.facebookAppId
  const googleAuthClientId = config.public.googleAuthClientId
  const appleAuthClientId = config.public.appleAuthClientId

  async function loginWithEmail(email: string, password: string) {
    const response = await API.loginWithEmail(email, password, getRequestLanguageCode())

    return response?.data
  }

  async function authenticateWithApple(): Promise<SocialAuthenticationResult> {
    if (!window.AppleID) {
      throw new Error('AppleID is not initialized')
    }

    if (!appleAuthClientId) {
      throw new Error('Apple auth client ID is not set')
    }

    window.AppleID.auth.init({
      clientId: appleAuthClientId,
      scope: 'email name',
      redirectURI: config.public.appleRedirect,
      state: new Date().getTime().toString(),
      usePopup: true
    })

    const appleSignInData = await window.AppleID.auth.signIn()

    if (!appleSignInData) {
      throw new Error('Could not sign in with Apple')
    }

    const verificationResponse = await API.verifyAppleUser(appleSignInData.authorization.id_token)

    if (!verificationResponse?.data) {
      throw new Error('Could not verify Apple user')
    }

    return {
      token: appleSignInData.authorization.id_token,
      verificationResponse: verificationResponse.data,
      type: 'apple'
    }
  }

  async function authenticateWithFacebook(): Promise<SocialAuthenticationResult> {
    if (!window.FB) {
      throw new Error('Facebook is not initialized')
    }

    if (!facebookAppId) {
      throw new Error('Facebook app ID is not set')
    }

    window.FB.init({
      appId: facebookAppId,
      cookie: true,
      xfbml: true,
      version: 'v20.0'
    })

    const authPromise = async () =>
      new Promise<FacebookAuthResponse>((resolve, reject) => {
        window.FB?.login(
          (response) => {
            if (response?.authResponse) {
              resolve(response.authResponse)
            } else {
              reject(new Error('Could not sign in with Facebook'))
            }
          },
          { scope: 'user_friends' }
        )
      })

    const authResponse = await authPromise()

    const verificationResponse = await API.verifyFacebookUser(authResponse.accessToken)

    if (!verificationResponse?.data) {
      throw new Error('Could not verify Facebook user')
    }

    return {
      token: authResponse.accessToken,
      verificationResponse: verificationResponse.data,
      type: 'facebook'
    }
  }

  async function initializeGoogleAuth(callback: (response: GoogleAuthResponse) => void) {
    if (!window.google) {
      throw new Error('Google is not initialized')
    }

    if (!googleAuthClientId) {
      throw new Error('Google auth client ID is not set')
    }

    window.google.accounts.id.initialize({
      client_id: googleAuthClientId,
      callback
    })
  }

  async function renderGoogleButton(containerElement: HTMLElement, width: string = '300px') {
    if (!window.google) {
      throw new Error('Google is not initialized')
    }

    if (!googleAuthClientId) {
      throw new Error('Google auth client ID is not set')
    }

    window.google.accounts.id.renderButton(containerElement, {
      type: 'standard',
      shape: 'rectangular',
      size: 'large',
      width,
      logo_alignment: 'center',
      text: 'continue_with'
    })
  }

  async function verifyGoogleToken(googleToken: string): Promise<SocialAuthenticationResult> {
    const verificationResponse = await API.verifyGoogleUser(googleToken)

    if (!verificationResponse?.data) {
      throw new Error('Could not verify Google user')
    }

    return {
      token: googleToken,
      verificationResponse: verificationResponse.data,
      type: 'google'
    }
  }

  async function createSessionWithAppleToken(
    token: string,
    email?: string,
    firstname?: string,
    lastname?: string,
    referrer?: string
  ) {
    const response = await API.createSessionWithAppleToken(
      token,
      getRequestLanguageCode(),
      email,
      firstname,
      lastname,
      referrer
    )

    if (!response?.data) {
      throw new Error('Could not create session with Apple')
    }

    return response.data
  }

  async function createSessionWithFacebookToken(
    token: string,
    email?: string,
    firstname?: string,
    lastname?: string,
    referrer?: string
  ) {
    const response = await API.createSessionWithFacebookToken(
      token,
      getRequestLanguageCode(),
      email,
      firstname,
      lastname,
      referrer
    )

    if (!response?.data) {
      throw new Error('Could not create session with Facebook')
    }

    return response.data
  }

  async function createSessionWithGoogleToken(
    token: string,
    email?: string,
    firstname?: string,
    lastname?: string,
    referrer?: string
  ) {
    const response = await API.createSessionWithGoogleToken(
      token,
      getRequestLanguageCode(),
      email,
      firstname,
      lastname,
      referrer
    )

    if (!response?.data) {
      throw new Error('Could not create session with Google')
    }

    return response.data
  }

  async function signupWithEmail(
    email: string,
    password: string,
    firstname: string,
    lastname: string,
    locale: string,
    referrer?: string
  ) {
    const response = await API.signup(email, password, firstname, lastname, locale, false, referrer)
    return response?.data
  }

  async function verifyUniqueEmail(email: string) {
    return await API.verifyUniqueEmail(email)
  }

  async function verifyEmail(userID: string, data: { email_token: string }) {
    const response = await API.verifyEmail(userID, data)
    return response.data
  }

  async function requestPasswordReset(email: string) {
    const response = await API.requestPasswordReset(email)
    return response.data
  }

  async function setPassword(password: string, token: string) {
    const response = await API.setPassword(password, token)
    return response.data
  }

  async function refreshSession() {
    const { data } = await API.refreshSession(getRequestLanguageCode())

    return data
  }

  function logout() {
    trackEvent(ANALYTICS_EVENTS.accountLogout())

    authStore.logout()
  }

  return {
    loginWithEmail,
    authenticateWithApple,
    authenticateWithFacebook,
    initializeGoogleAuth,
    renderGoogleButton,
    verifyGoogleToken,
    createSessionWithAppleToken,
    createSessionWithFacebookToken,
    createSessionWithGoogleToken,
    signupWithEmail,
    verifyUniqueEmail,
    verifyEmail,
    requestPasswordReset,
    setPassword,
    refreshSession,
    logout
  }
}
